/* eslint-disable max-lines */
import { useMutation, useQueryClient } from 'react-query';
import { useApiDelete, useApiGet, useApiPost, useApiQuery } from '../../../api';
import { useBuildVars } from '../../../config';
import { useEndUsersHeaders } from './end-user-headers';

export const EDStatus = {
  APPROVED: 'Approved',
  COMPLETED: 'Completed',
  DEACTIVATED: 'Deactivated',
  ESCALATED: 'Escalated - high priority',
  IN_PROCESS: 'In system process',
  IN_QUEUE: 'In system queue',
  NEW: 'New - edits needed',
  NEW_ENDUSER: 'New Pending EndUser',
  NEW_PENDING_SERVICE_PLAN: 'New Pending Service Plan',
  NEW_RESELLER: 'New Pending Reseller',
  PENDING_INCENTIVE: 'Pending Incentive Selection',
  PENDING_REVIEW: 'Pending review',
  REJECTED: 'Rejected - edits needed',
} as const;

export const EDStatusCode = {
  [EDStatus.IN_QUEUE]: 1,
  [EDStatus.IN_PROCESS]: 2,
  [EDStatus.COMPLETED]: 3,
  [EDStatus.ESCALATED]: 7,
  [EDStatus.REJECTED]: 8,
  [EDStatus.NEW]: 9,
  [EDStatus.PENDING_REVIEW]: 10,
  [EDStatus.APPROVED]: 11,
  [EDStatus.NEW_RESELLER]: 12,
  [EDStatus.NEW_ENDUSER]: 14,
  [EDStatus.DEACTIVATED]: 18,
  [EDStatus.PENDING_INCENTIVE]: 17,
  [EDStatus.NEW_PENDING_SERVICE_PLAN]: 16,
} as const;

export type ExceptionBidStatus = typeof EDStatus[keyof typeof EDStatus];

export interface ExceptionDealRequest {
  businessType?: string | null;
  countryCode: string;
  dealNumber?: string;
  enduserName?: string;
  filters: string;
  initialLoad?: boolean;
  maxuploadDate?: string;
  minuploadDate?: string;
  page: number;
  pageSize: number;
  resellerName?: string;
  sortingColumn?: string;
  sortingOrder?: string | null;
  uploadDate?: string;
  vendorName?: string;
  vendorid?: string;
}

export interface ExceptionBids {
  approvedate: string;
  customerseT_ID: number;
  deal_ingested_on: string;
  dealid: string;
  dealnumber: string;
  editor: string;
  editornotes: string;
  enduseraddress: string;
  enduserim: string;
  enduserimaddress: string;
  enduserondeal: string;
  esclatedsubmitdate: string;
  euaffiliationcount: string;
  importreferenceid: string;
  pendingreviewsubmitdate: string;
  rejecteddate: string;
  resellerim: string;
  resellerondeal: string;
  reviewer: string;
  reviewernotes: string;
  status: ExceptionBidStatus;
  vendorname: string;
  vendorprogramname: string;
  versionnumber: string;
}
export interface ExceptionDealResponse {
  exceptionbids: ExceptionBids[];
  page: number;
  pagesize: number;
  totalcount: number;
}

export interface ExceptionDealStatsResponse {
  activatedcount: number;
  approvedcount: number;
  deactivatedcount: number;
  escalatedcount: number;
  newcount: number;
  newendusercount: number;
  newresellercount: number;
  pendingreviewcount: number;
  rejectedcount: number;
  totalcount: number;
}

export function useGetExceptionDeals(params: ExceptionDealRequest) {
  const buildVars = useBuildVars();
  return useApiQuery<ExceptionDealResponse>(
    '/system.webapi.gds/api/v1/GdsCoreApiExceptionBids/searchExceptionBids',
    params,
    undefined,
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export function useGetArchiveExceptionDeals(params: ExceptionDealRequest) {
  const buildVars = useBuildVars();
  return useApiQuery<ExceptionDealResponse>(
    '/system.webapi.gds/api/v1/GdsCoreApiExceptionBids/searchArchivedDeals',
    params,
    undefined,
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export function useGetExceptionDealsStats(params: ExceptionDealRequest) {
  const buildVars = useBuildVars();
  return useApiQuery<ExceptionDealStatsResponse>(
    '/system.webapi.gds/api/v1/GdsCoreApiExceptionBids/exceptionBidStats',
    params,
    undefined,
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}
export interface EDApproveRejectRequest {
  businessType: string;
  customersetids: string;
  dealids: string;
  dealnumbers: string;
  dealversions: string;
  importreferenceids: string;
  isapproved: boolean;
  notes: string;
  servicePlanResolved: boolean;
  userid: string;
  vendornames: string;
}
export interface EDApproveRejectResponse {
  message: string;
  statusCode: number;
}
export function useGetEDApproveReject(importreferenceid?: string) {
  const buildVars = useBuildVars();
  const queryClient = useQueryClient();
  const apiGet = useApiGet<EDApproveRejectResponse>({
    host: buildVars.VP_API_BASE,
  });
  return useMutation(
    (params: EDApproveRejectRequest) => {
      return apiGet(
        '/system.webapi.gds/api/v1/GdsCoreApiExceptionBids/approveRejectExceptionBids',
        {
          ...params,
        }
      );
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries(
            importreferenceid
              ? `/system.webapi.gds/api/v1/GdsCoreApiDeals/${importreferenceid}`
              : '/system.webapi.gds/api/v1/GdsCoreApiExceptionBids/searchExceptionBids'
          )
          .catch(() => undefined);
      },
    }
  );
}

export interface EscalateRequest {
  dealid: string;
  importreferenceid: string;
  notes: string;
  userid: string;
}
export interface EscalateResponse {
  message: string;
  statusCode: number;
}

export function useEscalateED(request: EscalateRequest) {
  const buildVars = useBuildVars();
  const queryClient = useQueryClient();
  const apiPost = useApiPost({ host: buildVars.VP_API_BASE });
  return useMutation(
    () =>
      apiPost(
        '/system.webapi.gds/api/v1/GdsCoreApiExceptionBids/SaveEscalationNotes',
        request
      ),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(
          '/system.webapi.gds/api/v1/GdsCoreApiExceptionBids/searchExceptionBids'
        ),
    }
  );
}

export interface DefaultResellerRequest {
  dealnumber: string;
  dealversion: number;
  reseller_name: string;
  reseller_no: string;
  user_id: string;
  vendor_id: number;
  vendor_program_id: number;
}

export function useDefaultReseller(request: DefaultResellerRequest) {
  const buildVars = useBuildVars();
  const queryClient = useQueryClient();
  const apiPost = useApiPost({ host: buildVars.VP_API_BASE });
  return useMutation(
    (flag: boolean) =>
      apiPost('/system.webapi.gds/api/v1/GdsCoreApiDeals/SaveDefaultReseller', {
        ...request,
        flag,
      }),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(
          '/system.webapi.gds/api/v1/GdsCoreApiDeals/DefaultBCNList'
        ),
    }
  );
}

export interface IMSKURequest {
  dealnumber: string;
  dealversion: number;
  newImSku: string;
  oldImSku: string;
  skSbo: number;
  trackingID: number;
}
export interface IncentiveRequest {
  dealnumber: string;
  dealversion: number;
  newImSku: string;
  oldImSku: string;
  skSbo: number;
  trackingID: number;
}
export interface LineBtlIncentives {
  additional_attributes?: string;
  baseSales: boolean;
  discountDetailLineId: number;
  productSetLineId: number;
  proficiency: boolean;
  trackingID: number;
}

export type UpdateBtlIncentiveSelectionRequest = {
  dealnumber: string;
  lineBtlIncentives: LineBtlIncentives[];
  skSbo: number;
  vendorprogramid: number;
};

export function useSaveIMSKU(request: IMSKURequest, importreferenceid: string) {
  const buildVars = useBuildVars();
  const queryClient = useQueryClient();
  const apiPost = useApiPost({ host: buildVars.VP_API_BASE });
  return useMutation(
    () =>
      apiPost('/system.webapi.gds/api/v1/GdsCoreApiDeals/UpdateIMSKU', request),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(
          `/system.webapi.gds/api/v1/GdsCoreApiDeals/${importreferenceid}`
        ),
    }
  );
}
export function useSaveIncentiveSelection(importreferenceid: string) {
  const buildVars = useBuildVars();
  const queryClient = useQueryClient();
  const apiPost = useApiPost({ host: buildVars.VP_API_BASE });
  return useMutation(
    (payload: UpdateBtlIncentiveSelectionRequest) =>
      apiPost(
        '/system.webapi.gds/api/v1/GdsCoreApiExceptionBids/UpdateBtlIncentives',
        payload
      ),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(
          `/system.webapi.gds/api/v1/GdsCoreApiDeals/${importreferenceid}`
        ),
    }
  );
}
export interface EndUserSearchRequest {
  'IM-CustomerNumber': string;
  countryCode: string;
}

export interface Address {
  acopEndUserId: number;
  addressId: number;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressType: null;
  city: string;
  cmpEndCustomerId: string;
  countryCode: string;
  erpAddressId: number;
  erpType: string;
  ingramEndUserId: number;
  postalCode: string;
  resellerEndUserId: string;
  state: string;
}
export interface ContactInfo {
  contactId: number;
  contactName: string;
  email: string;
  erpContactId: number;
  faxNumber: number;
  phoneExtension: number;
  phoneNumber: string;
  title: string;
}
export interface DirectshipeEnduser {
  directshipheaderenduseraddressid: string;
  directshipheaderendusercontactid: string;
  directshipheaderenduserid: string;
}
export interface ErpEndUserIdReferences {
  resellerEndUserId: number;
}
export interface EndUserSearchResponse {
  address: Address[];
  contactInfo: ContactInfo[];
  directshipeEnduser: DirectshipeEnduser;
  endUserId: number | null;
  endUserName: string;
  erpEndUserIdReferences: ErpEndUserIdReferences;
  institutionType: string;
  marketSegmentType: string;
}
export function useEndUserSearch(request: EndUserSearchRequest) {
  const buildVars = useBuildVars();
  const normalizedCountryCode =
    request.countryCode === 'UK' ? 'GB' : request.countryCode;
  const headers = useEndUsersHeaders(
    request['IM-CustomerNumber'],
    normalizedCountryCode
  );
  const apiPost = useApiPost<EndUserSearchResponse[]>({
    host: buildVars.EU_BASE,
  });
  return useMutation(
    (params: {
      acopEndUserId?: number;
      endUserId?: number;
      endUserName?: string;
      erpType?: string;
      resellerEndUserId?: number;
      searchType?: string;
    }) =>
      apiPost(
        '/system.api.enduser/v2/endusers',
        {
          ...params,
          erpType: params.erpType ?? 'I',
        },
        undefined,
        headers
      )
  );
}
export interface ImSkuListRequest {
  DiscountDetailId: number;
  EnableRulesEngine: boolean;
  IsXcmVendor: boolean;
  SkSbo: number;
  TrackingID: number;
}
export interface ImSkuList {
  id: number;
  imsku: string;
}
export interface ImSkuListResponse {
  listGetImSku: ImSkuList[];
  message: string;
  transactionID: string;
  transactionStatus: string;
}
export function useGetImSkuList(params: ImSkuListRequest) {
  const buildVars = useBuildVars();
  return useApiQuery<ImSkuListResponse>(
    '/system.webapi.gds/api/v1/GdsCoreApiDeals/GetImSku',
    params,
    undefined,
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}

export interface DeleteImSkuRequest {
  dealnumber: string;
  dealversion: number;
  imSku: string;
  isDeleteAll: boolean;
  skSbo: number;
  skSboProductset: number;
  trackingID: number;
}

export function useDeleteIMSKU(
  request: DeleteImSkuRequest,
  importreferenceid: any
) {
  const buildVars = useBuildVars();
  const apiDelete = useApiDelete(buildVars.VP_API_BASE);
  const queryClient = useQueryClient();
  return useMutation(
    () => {
      return apiDelete(
        `/system.webapi.gds/api/v1/GdsCoreApiDeals/DeleteImSku`,
        request
      );
    },
    {
      onSettled: () =>
        queryClient.invalidateQueries(
          '/system.webapi.gds/api/v1/GdsCoreApiDeals/GetImSku'
        ),
      onSuccess: () =>
        queryClient.invalidateQueries(
          `/system.webapi.gds/api/v1/GdsCoreApiDeals/${importreferenceid}`
        ),
    }
  );
}

export interface DefaultSKURequest {
  sk_sbo_group: number;
  sku_no: string;
  vendor_number: string;
  vpn: string;
}

export function useDefaultSKU(request: DefaultSKURequest) {
  const buildVars = useBuildVars();
  const apiPost = useApiPost({ host: buildVars.VP_API_BASE });
  return useMutation((flag: boolean) =>
    apiPost('/system.webapi.gds/api/v1/GdsCoreApiDeals/SaveDefaultSKU', {
      ...request,
      flag,
    })
  );
}

export const getERPType = (isSubscription: boolean, isHybrid: boolean) => {
  if (isHybrid) return 'IC';
  if (isSubscription) return 'C';
  return 'I';
};
